import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Waves } from "lucide-react";
import api from "@/lib/api";
import log from "@/lib/log";
import { FiltersBar, StatCards, TrendChart, BreakdownLists } from "./components/StatsOverview";
import { CheckupResultsTable, LeadsTable } from "./components/LeadTables";
import { PartnersPanel, RemindersPanel } from "./components/PartnersPanel";
import { SettingsPanel } from "./components/SettingsPanel";

const EMPTY_FILTERS = { from_date: "", to_date: "", referral_source: "", campaign: "", state: "" };

function useAdminData(appliedFilters) {
  const navigate = useNavigate();
  const [data, setData] = useState({ stats: null, leads: [], settings: null, partners: [], reminders: null, checkups: [] });
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const params = Object.fromEntries(Object.entries(appliedFilters).filter(([, v]) => v));
      const [s, l, cfg, p, r, c] = await Promise.all([
        api.get("/admin/stats", { params }),
        api.get("/admin/leads"),
        api.get("/admin/settings"),
        api.get("/admin/partners"),
        api.get("/admin/reminders"),
        api.get("/admin/assessments"),
      ]);
      if (seq !== requestSeq.current) return; // a newer load superseded this one — never apply stale data
      setData({ stats: s.data, leads: l.data.leads, settings: cfg.data, partners: p.data.partners, reminders: r.data, checkups: c.data.results });
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login", { replace: true });
      else log.warn("Admin data load failed", e);
    }
  }, [appliedFilters, navigate]);

  useEffect(() => {
    load();
    return () => {
      requestSeq.current += 1; // invalidate in-flight requests on filter change / unmount
    };
  }, [load]);

  const setLeads = useCallback((updater) => setData((d) => ({ ...d, leads: typeof updater === "function" ? updater(d.leads) : updater })), []);
  return { ...data, load, setLeads };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const { stats, leads, settings, partners, reminders, checkups, load, setLeads } = useAdminData(appliedFilters);

  const logout = async () => {
    await api.post("/auth/logout").catch((e) => log.warn("Logout request failed", e));
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-navy text-cream sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
              <Waves className="w-4 h-4 text-navy" strokeWidth={2.4} />
            </span>
            <span className="font-serif text-xl font-semibold">Legacy Wave <span className="text-gold text-sm font-sans font-bold uppercase tracking-widest ml-1">Admin</span></span>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm font-bold text-cream/80 hover:text-gold transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8 space-y-8">
        <FiltersBar filters={filters} onChange={setFilters} onApply={() => setAppliedFilters(filters)} />
        <StatCards stats={stats} />
        <TrendChart trend={stats?.trend} />
        <BreakdownLists stats={stats} />
        <CheckupResultsTable checkups={checkups} />
        <LeadsTable leads={leads} onLeadsChange={setLeads} onRefresh={load} />
        <div className="grid lg:grid-cols-2 gap-4">
          <PartnersPanel partners={partners} onRefresh={load} />
          <RemindersPanel reminders={reminders} />
        </div>
        <SettingsPanel settings={settings} onSaved={load} />
      </div>
    </div>
  );
}
